import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Chip,
  LinearProgress,
  Tooltip,
  Card,
  CardContent,
  Alert,
  IconButton,
  Collapse,
} from '@mui/material';
import {
  CheckCircle as HealthyIcon,
  Warning as DegradedIcon,
  Error as UnhealthyIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  Storage as DatabaseIcon,
  Memory as RedisIcon,
  Work as CeleryIcon,
  ElectricBolt as CircuitIcon,
  Groups as PoolIcon,
  Link as ConnectionIcon,
} from '@mui/icons-material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

interface HealthCheck {
  status: 'healthy' | 'degraded' | 'unhealthy';
  [key: string]: any;
}

interface SystemHealthData {
  status: 'healthy' | 'degraded' | 'unhealthy';
  timestamp: string;
  checks: {
    database?: HealthCheck;
    redis?: HealthCheck;
    celery?: HealthCheck;
    circuit_breakers?: HealthCheck;
    agent_pools?: HealthCheck;
    connection_pools?: HealthCheck;
  };
}

const statusColors = {
  healthy: 'success',
  degraded: 'warning',
  unhealthy: 'error',
} as const;

const statusIcons = {
  healthy: <HealthyIcon />,
  degraded: <DegradedIcon />,
  unhealthy: <UnhealthyIcon />,
};

const componentIcons = {
  database: <DatabaseIcon />,
  redis: <RedisIcon />,
  celery: <CeleryIcon />,
  circuit_breakers: <CircuitIcon />,
  agent_pools: <PoolIcon />,
  connection_pools: <ConnectionIcon />,
};

interface SystemHealthProps {
  autoRefresh?: boolean;
  refreshInterval?: number;
  compact?: boolean;
}

export default function SystemHealth({ autoRefresh = true, refreshInterval = 5000, compact = false }: SystemHealthProps) {
  const [healthData, setHealthData] = useState<SystemHealthData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    loadHealthData();
    
    if (autoRefresh) {
      const interval = setInterval(loadHealthData, refreshInterval);
      return () => clearInterval(interval);
    }
  }, [autoRefresh, refreshInterval]);

  const loadHealthData = async () => {
    try {
      const response = await axios.get(`${API_URL.replace('/api/v1', '')}/health`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
        },
      });
      setHealthData(response.data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load system health');
      // Set unhealthy state on error
      setHealthData({
        status: 'unhealthy',
        timestamp: new Date().toISOString(),
        checks: {},
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading && !healthData) {
    return (
      <Box sx={{ width: '100%' }}>
        <LinearProgress />
      </Box>
    );
  }

  if (compact) {
    return (
      <Tooltip title={`System Status: ${healthData?.status || 'unknown'}`}>
        <Chip
          icon={statusIcons[healthData?.status || 'unhealthy']}
          label={healthData?.status?.toUpperCase() || 'UNKNOWN'}
          color={statusColors[healthData?.status || 'unhealthy']}
          size="small"
          onClick={() => setExpanded(!expanded)}
        />
      </Tooltip>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {statusIcons[healthData?.status || 'unhealthy']}
          System Health
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Chip
            label={healthData?.status?.toUpperCase() || 'UNKNOWN'}
            color={statusColors[healthData?.status || 'unhealthy']}
            size="small"
          />
          <IconButton size="small" onClick={loadHealthData}>
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {healthData && (
        <>
          <Grid container spacing={2}>
            {Object.entries(healthData.checks || {}).map(([component, check]) => (
              <Grid item xs={12} sm={6} md={4} key={component}>
                <Card variant="outlined">
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      {componentIcons[component as keyof typeof componentIcons]}
                      <Typography variant="subtitle2" sx={{ textTransform: 'capitalize' }}>
                        {component.replace(/_/g, ' ')}
                      </Typography>
                    </Box>
                    <Chip
                      icon={statusIcons[check.status]}
                      label={check.status.toUpperCase()}
                      color={statusColors[check.status]}
                      size="small"
                      sx={{ width: '100%' }}
                    />
                    {check.latency_ms && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        Latency: {check.latency_ms}ms
                      </Typography>
                    )}
                    {check.worker_count !== undefined && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        Workers: {check.worker_count}
                      </Typography>
                    )}
                    {check.open_count !== undefined && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        Open  Breakers: {check.open_count}/{check.total_count}
                      </Typography>
                    )}
                    {check.healthy_pools !== undefined && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        Healthy Pools: {check.healthy_pools}/{check.total_pools}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {healthData.timestamp && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block', textAlign: 'right' }}>
              Last updated: {new Date(healthData.timestamp).toLocaleString()}
            </Typography>
          )}
        </>
      )}
    </Paper>
  );
}
